import { useEffect, useState } from 'react';
import { Link } from 'react-admin';
import { clsx } from 'clsx';
import { Button } from '@mui/material';

import { Credit, CreditFarm } from '@types';
import { InitialCard } from '@components';
import cls from './CreditFarms.module.css';

interface Props {
  credit: Credit;
}

interface FarmProps {
  farm: CreditFarm;
  creditId?: number;
  className?: string;
}

const ArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none">
    <path d="M6.99931 5.97195C6.99423 5.76707 6.90904 5.57142 6.76016 5.42265L1.50832 0.254521C1.29067 0.0422583 0.96985 -0.0448615 0.666228 0.0263848C0.362606 0.0976311 0.121896 0.316538 0.0347567 0.600416C-0.0523834 0.884294 0.0269408 1.19069 0.243423 1.40443L4.91381 5.99779L0.243424 10.5911C0.026941 10.8049 -0.0523833 11.1113 0.0347568 11.3952C0.121896 11.6794 0.362606 11.8979 0.666228 11.9692C0.96985 12.0404 1.29067 11.9533 1.50832 11.7411L6.76016 6.57293C6.92233 6.41124 7.00869 6.19454 6.99931 5.97231L6.99931 5.97195Z" fill="white"/>
  </svg>
);

const SuccessIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect width="16" height="16" rx="8" fill="#275D3C"/>
    <path d="M7.42773 9.57129L10.9992 5.99981" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    <path d="M7.42969 9.57129L6.0011 8.1427" stroke="white" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const ErrorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect width="16" height="16" rx="8" fill="#D32F2F"/>
    <rect x="3.75781" y="5.17188" width="2" height="10" rx="1" transform="rotate(-45 3.75781 5.17188)" fill="white"/>
    <rect x="10.8281" y="3.75781" width="2" height="10" rx="1" transform="rotate(45 10.8281 3.75781)" fill="white"/>
  </svg>
);

const Farm = ({ farm, creditId }: FarmProps) => {
  const isCompleted =
    (farm?.crops || []).length > 0
      && !!farm?.totalArea
      && !!farm?.siteHa
      && !!farm?.cycle
      && !!farm?.sowingDate
      && !!farm?.effectiveDate
      && !!farm?.endOfTermDate
      && !!farm?.siteOwner
      && !!farm?.yield
      && !!farm?.state
      && !!farm?.latitude
      && !!farm?.longitude
      && !!farm?.irrigation
  ;
  const hasFieldReport = !!farm?.fieldReportFile;
  const latitudeNumber = +farm?.latitude;
  const longitudeNumber = +farm?.longitude;
  const latitude = !isNaN(latitudeNumber) ? latitudeNumber.toFixed(5) : farm?.latitude;
  const longitude = !isNaN(longitudeNumber) ? longitudeNumber.toFixed(5) : farm?.longitude;

  return (
    <div className={cls.crop}>
      <div className={cls.cropContent}>
        <div className={cls.props}>
          <div className={cls.prop}>
            <span>Crop:</span>
            <span>{farm?.cropsTitle}</span>
          </div>
          <div className={cls.prop}>
            <span>Coordinate:</span>
            <span>{latitude}, {longitude}</span>
          </div>
          <div className={cls.prop}>
            <span>State:</span>
            <span>{farm?.state}</span>
          </div>
          <div className={cls.prop}>
            <span>Total area:</span>
            <span>{farm?.totalArea ? `${farm?.totalArea} Ha` : ''}</span>
          </div>
        </div>
        <div className={cls.statuses}>
          <div className={cls.status}>
            {isCompleted ? <SuccessIcon /> : <ErrorIcon />}
            <span>Completed Data</span>
          </div>
          <div className={cls.status}>
            {hasFieldReport ? <SuccessIcon /> : <ErrorIcon />}
            <span>Field Report Uploaded</span>
          </div>
        </div>
      </div>
      <div className={cls.cropActions}>
        <Link to={`/credit/${creditId}?farm=${farm?.id}&farms`}>
          <Button className={cls.cropBtn}>Open Data</Button>
        </Link>
      </div>
    </div>
  );
};

const CreditFarmsNavigation = ({ currentFarmIndex, count, prev, next }: any) => {
  const isFirst = currentFarmIndex === 0;
  const isLast = currentFarmIndex === count - 1;

  return (
    <div className={cls.nav}>
      <div className={clsx(cls.navItem, cls.prev, isFirst && cls.disabled)} onClick={prev}>
        <ArrowIcon />
      </div>
      <div className={cls.counter}>
        <span>{currentFarmIndex + 1}</span>/{count}
      </div>
      <div className={clsx(cls.navItem, cls.next, isLast && cls.disabled)} onClick={next}>
        <ArrowIcon />
      </div>
    </div>
  );
};

export const CreditFarms = ({ credit }: Props) => {
  const [farms, setFarms] = useState<CreditFarm[]>([]);
  const [currentFarmIndex, setCurrentFarmIndex] = useState<number>(0);

  const prev = () => {
    if (currentFarmIndex > 0) {
      setCurrentFarmIndex(currentFarmIndex - 1);
    }
  };

  const next = () => {
    if (currentFarmIndex < farms.length - 1) {
      setCurrentFarmIndex(currentFarmIndex + 1);
    }
  };

  useEffect(() => {
    setFarms(credit?.farms || []);
  }, [credit.farms]);

  return (
    <InitialCard
      left="Farms and Insurance"
      right={
        <CreditFarmsNavigation
          currentFarmIndex={currentFarmIndex}
          count={(credit?.farms || []).length}
          prev={prev}
          next={next}
        />
      }
      contentClass={cls.content}
      wrapperClass={cls.wrap}
      extraPadding
      minHeight={308}
    >
      <Farm farm={farms[currentFarmIndex]} creditId={credit?.id} />
    </InitialCard>
  );
};
